'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { LanguageSelector } from '@/contexts/application/components/layout/LanguageSelector';
import { PortalProfileControl } from '@/contexts/application/components/layout/PortalProfileControl';
import { ThemeToggle } from '@/shared/components/theme';
import { useCurrentUser } from '@/contexts/authentication/client';

interface PublicHeaderProps {
  className?: string;
}

/**
 * A long landing gets its sections in the header.
 *
 * The campaign page is one scroll after another, and a visitor who wants the
 * example or the print price had no way to them but the wheel. These are
 * in-page anchors, not routes: they belong to the page, so the header only
 * shows them while that page is open.
 */
const CAMPAIGN_SECTIONS = [
  { id: 'how', key: 'campaignHow' },
  { id: 'example', key: 'campaignExample' },
  { id: 'print', key: 'campaignPrint' },
  { id: 'safety', key: 'campaignSafety' },
] as const;

export function PublicHeader({ className }: PublicHeaderProps) {
  const locale = useLocale();
  const t = useTranslations('nav');
  const { isAuthenticated } = useCurrentUser();
  const pathname = usePathname() ?? '';
  const onCampaign = /\/new-year\/?$/.test(pathname);

  return (
    <header data-testid="public-header" className={`h-16 sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border ${className ?? ''}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        {/* Logo + public nav */}
        <div className="flex items-center gap-4">
          <Link href={`/${locale}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <span className="text-2xl">🦉🐱</span>
            <span className="hidden sm:inline font-bold text-foreground">OwlCat Club</span>
          </Link>
          {onCampaign ? (
            /* The page's own table of contents, and the button that does what
               the page is for. Hidden on a phone: four anchors and a CTA do
               not fit beside the logo, and the page carries its own sticky
               button down there. */
            <nav className="hidden items-center gap-4 md:flex" data-testid="public-header-campaign-nav">
              {CAMPAIGN_SECTIONS.map(({ id, key }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                  data-testid={`public-header-section-${id}`}
                >
                  {t(key)}
                </a>
              ))}
            </nav>
          ) : isAuthenticated ? (
            <Link
              href={`/${locale}`}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="public-header-app-link"
            >
              {t('myStories')}
            </Link>
          ) : (
            /* A visitor looks for the price in the header before anywhere else —
               and until now the only way to it was to scroll the whole landing. */
            <>
              <Link
                href={`/${locale}/gallery`}
                className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline"
                data-testid="public-header-gallery-link"
              >
                {t('gallery')}
              </Link>
              <Link
                href={`/${locale}/pricing`}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                data-testid="public-header-pricing-link"
              >
                {t('pricing')}
              </Link>
            </>
          )}
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-3">
          {onCampaign && (
            <a
              href="#anketa"
              className="hidden rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 md:inline-flex"
              data-testid="public-header-campaign-cta"
            >
              {t('campaignStart')}
            </a>
          )}
          <LanguageSelector variant="select" />
          <ThemeToggle />

          {/* Fleet-shared identity control (logged in as X / Sign in) */}
          <PortalProfileControl />
        </div>
      </div>
    </header>
  );
}
